import { useEffect, useRef, useState } from "react";
import { startWhep } from "../lib/whep";
import { RELAY_CHANNELS, whepUrl } from "../channels";

type Status = "connecting" | "playing" | "no-signal";

interface LiveFeedProps {
  /** Path inicial de MediaMTX. Default: primer canal. */
  initialChannelId?: string;
  /** Mostrar el selector de canal. Default: true. */
  showChannelSelector?: boolean;
  className?: string;
}

/**
 * Reproductor del feed en vivo del relay vía WHEP (WebRTC).
 *
 * - Reconecta al cambiar de canal.
 * - Auto-reintenta cada 3s ante "sin señal" (path on-demand que aún no arrancó,
 *   o stream caído) — mismo patrón que LiveMonitor del relay.
 * - Arranca muteado: Chrome bloquea autoplay con audio; el usuario desmutea con
 *   el control nativo del <video>.
 */
export function LiveFeed({
  initialChannelId = RELAY_CHANNELS[0]?.id,
  showChannelSelector = true,
  className,
}: LiveFeedProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);

  const [channelId, setChannelId] = useState(initialChannelId);
  const [status, setStatus] = useState<Status>("connecting");
  const [error, setError] = useState("");
  const [retryNonce, setRetryNonce] = useState(0);

  // (Re)conectar al cambiar de canal o al reintentar.
  useEffect(() => {
    if (!videoRef.current || !channelId) return;

    const ac = new AbortController();
    let disposed = false;

    setStatus("connecting");
    setError("");

    startWhep(whepUrl(channelId), videoRef.current, ac.signal)
      .then((pc) => {
        if (disposed) {
          pc.close();
          return;
        }
        pcRef.current = pc;
        pc.onconnectionstatechange = () => {
          const st = pc.connectionState;
          if (st === "connected") setStatus("playing");
          else if (st === "failed" || st === "disconnected") setStatus("no-signal");
        };
      })
      .catch((e) => {
        if (disposed || ac.signal.aborted) return;
        setStatus("no-signal");
        setError(String(e));
      });

    return () => {
      disposed = true;
      ac.abort();
      pcRef.current?.close();
      pcRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [channelId, retryNonce]);

  // Auto-reconexión cada 3s ante "sin señal".
  useEffect(() => {
    if (status !== "no-signal") return;
    const t = setTimeout(() => setRetryNonce((n) => n + 1), 3000);
    return () => clearTimeout(t);
  }, [status]);

  return (
    <div className={`flex flex-col gap-2 ${className ?? ""}`}>
      {showChannelSelector && RELAY_CHANNELS.length > 1 && (
        <div className="flex flex-wrap items-center gap-1">
          {RELAY_CHANNELS.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setChannelId(c.id)}
              className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                c.id === channelId
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      <div className="relative aspect-video w-full overflow-hidden rounded bg-black">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          controls
          muted
          className="h-full w-full object-contain"
        />
        {status !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-sm text-white">
            {status === "connecting" ? (
              <span>Conectando…</span>
            ) : (
              <>
                <span>Sin señal — reconectando…</span>
                {error && (
                  <span className="max-w-md px-4 text-center text-xs text-white/60">{error}</span>
                )}
                <button
                  type="button"
                  onClick={() => setRetryNonce((n) => n + 1)}
                  className="rounded bg-white/20 px-3 py-1 text-xs hover:bg-white/30"
                >
                  Reintentar
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
